import React, { useState } from 'react';
import Modal from '../Modal';
import ShareSocial from '../ShareSocial';
import SubscriberLink from '../forms/SubscriberLink';

interface FDIInflowsDataType {
    tagline: string;
    heading: string;
    description: string;
    downloadLink: string;
    downloadLabel: string;
    cards: FDIInflowsCardType[];
}

interface FDIInflowsCardType {
    id: string; // For dynamic ID assignment
    className?: string; // For dynamic class assignment
    title?: string;
    titleStrong?: string;
    description?: string;
    icon?: string;
    imageUrl?: string;
    bullets?: BulletPointType[];
}

interface BulletPointType {
    id: string; // For bullet ID
    icon: string;
    className?: string; // To dynamically assign classes to the bullet
    title: string;
    description: string;
}


// Interface for FDI inflows section props
interface FDIInflowsProps {
    data: FDIInflowsDataType;
}

// Component for FDI inflows section
const FDIInflows: React.FC<FDIInflowsProps> = ({ data }) => {
    const [modalImageUrl, setModalImageUrl] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const openModal = (imageUrl: string) => {
        setModalImageUrl(imageUrl);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setModalImageUrl(null);
    };

    return (
        <section id="fdi-inflows" className="section-regular bg-brown-copy">
            <div className="container-large">
                <div className="bento-w-heading">
                    <div className="heading-wrap">
                        <div className="limited-heading-set">
                            <div className="tagline-heading">{data.tagline}</div>
                            <div className="heading-set-3">
                                <h2 className="h3-heading">{data.heading}</h2>
                                <div className="description">{data.description}</div>
                            </div>
                        </div>
                        <div className="action-side">
                            <SubscriberLink href={data.downloadLink} target="_blank" className="button-left-icon w-inline-block">
                                <div className="button-icon-left"></div>
                                <div className="text-block-4">{data.downloadLabel}</div>
                            </SubscriberLink>
                            <ShareSocial />
                        </div>
                    </div>
                    <div className="bento-grid-wrapper">
                        <div className="w-layout-grid bento-grid _3-columns">
                            {data.cards.map((card, index) => (
                                <div key={index} id={card.id} className={`bento-card ${card.className}`}>
                                    <div className="bento-card-header">
                                        <div className="wrap-v-small">
                                            {card.title && <h3 className="h4-heading">
                                                {card.title}
                                                {card.titleStrong && <span className='text-span'><strong>{card.titleStrong}</strong></span>}
                                            </h3>}
                                            {card.description && <p className="description">{card.description}</p>}
                                            {card.imageUrl && <div className='image-container'><img src={card.imageUrl} alt="" className="bento-card-image" onClick={() => openModal(card.imageUrl || "")}
                                                style={{ cursor: 'pointer' }} /></div>}
                                            {card.icon && <div className="text-icon reddish">{card.icon}</div>}
                                            {card.bullets && (
                                                <div className="w-layout-grid bullets-with-icons">
                                                    {card.bullets.map((bullet, i) => (
                                                        <div key={i} id={bullet.id} className={`content-feature-wrap ${bullet.className}`}>
                                                            <div className={`text-icon ${bullet.className}`}>
                                                                {bullet.icon}
                                                            </div>
                                                            <div className="wrap-v-x-small">
                                                                <div className="paragraph-large-2 text-weight-medium">
                                                                    <strong className="bold-text">{bullet.title}</strong>
                                                                </div>
                                                                <p className="paragraph-small">{bullet.description}</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            {isModalOpen && (
                <Modal isOpen={isModalOpen} onClose={closeModal}>
                    {/* Pass dynamic content here */}
                    <img src={modalImageUrl || ""} />
                </Modal>
            )}
        </section>
    );
};

export default FDIInflows;
